import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar } from "lucide-react";
import { api, jobPhotoSrc } from "../lib/api";
import { CompareSlider } from "../components/CompareSlider";
import { usePageTitle } from "../hooks/usePageTitle";

const PHASE_ORDER = { before: 0, during: 1, after: 2 };
const PHASE_LABEL = { before: "Before", during: "During", after: "After" };

function JobGroupCard({ group, idx }) {
  const sorted = [...group].sort((a, b) => (PHASE_ORDER[a.phase] ?? 9) - (PHASE_ORDER[b.phase] ?? 9));
  const [active, setActive] = useState(0);
  const before = sorted.find((p) => p.phase === "before");
  const after = sorted.find((p) => p.phase === "after");
  const during = sorted.filter((p) => p.phase === "during");
  const hasCompare = Boolean(before && after);
  const j = hasCompare ? after : sorted[active];
  const hasPhases = !hasCompare && sorted.length > 1;

  return (
    <article data-testid={`job-card-${idx}`} className="group bg-white border-2 border-[#2C3539]/10 hover:border-[#2F5F80] transition-colors overflow-hidden">
      {hasCompare ? (
        <>
          <CompareSlider beforeSrc={jobPhotoSrc(before)} afterSrc={jobPhotoSrc(after)} alt={j.job_site_name} idx={idx} />
          {during.length > 0 && (
            <div className="bg-[#F1F3F5] px-3 py-2.5 border-t border-[#2C3539]/10" data-testid={`job-during-strip-${idx}`}>
              <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#2C3539]/60 mb-1.5">During the job</div>
              <div className="flex gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {during.map((p) => (
                  <img key={p.id} src={jobPhotoSrc(p)} alt={`${p.job_site_name} — during`} className="w-20 h-14 object-cover shrink-0 border border-[#2C3539]/15" loading="lazy" />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="relative aspect-[4/3] overflow-hidden bg-[#2C3539]">
          <img
            src={jobPhotoSrc(j)}
            alt={j.job_site_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          {j.phase && (
            <span className="absolute top-3 left-3 bg-[#2F5F80] text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1" data-testid={`job-phase-badge-${idx}`}>
              {PHASE_LABEL[j.phase]}
            </span>
          )}
          {hasPhases && (
            <div className="absolute bottom-3 left-3 right-3 flex gap-1.5" data-testid={`job-phase-tabs-${idx}`}>
              {sorted.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActive(i)}
                  data-testid={`job-phase-tab-${idx}-${i}`}
                  className={`flex-1 text-[10px] font-bold uppercase tracking-[0.12em] py-1.5 transition-colors ${i === active ? "bg-[#2F5F80] text-white" : "bg-black/50 text-white/80 hover:bg-black/70"}`}
                >
                  {p.phase ? PHASE_LABEL[p.phase] : `Photo ${i + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="p-6">
        <div className="text-[#2F5F80] text-[10px] tracking-[0.3em] font-bold uppercase">{j.service_type}</div>
        <h3 className="font-display text-2xl uppercase mt-1 text-[#2C3539] leading-tight">{j.job_site_name}</h3>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#2C3539]/70">
          <span className="inline-flex items-center gap-1"><MapPin size={14} className="text-[#2F5F80]"/> {j.location}</span>
          <span className="inline-flex items-center gap-1"><Calendar size={14} className="text-[#2F5F80]"/> {j.date}</span>
        </div>
        {j.description && <p className="mt-3 text-sm text-[#2C3539]/80 line-clamp-3">{j.description}</p>}
      </div>
    </article>
  );
}

export default function PastJobs() {
  usePageTitle("Past Jobs — Before & After", "Real before, during and after photos from AV Contracting excavation, concrete, retaining wall and drainage projects across Vancouver Island and the Lower Mainland.");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data || [])).finally(() => setLoading(false));
  }, []);

  const merged = jobs;

  const filtered = useMemo(() => {
    if (filter === "all") return merged;
    return merged.filter((j) => j.service_type === filter);
  }, [merged, filter]);

  // Photos with the same job site name group into one card (before/during/after)
  const groups = useMemo(() => {
    const map = new Map();
    for (const j of filtered) {
      const key = `${(j.job_site_name || "").trim().toLowerCase()}|${(j.location || "").trim().toLowerCase()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(j);
    }
    return Array.from(map.values());
  }, [filtered]);

  const services = useMemo(() => {
    const set = new Set(merged.map((j) => j.service_type));
    return ["all", ...Array.from(set)];
  }, [merged]);

  return (
    <div className="pt-20 md:pt-24" data-testid="past-jobs-page">
      <section className="bg-[#2C3539] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase mb-3">Past Jobs</div>
            <h1 className="font-display text-6xl md:text-7xl uppercase leading-[0.92]">Our work,<br/>across BC.</h1>
          </div>
          <div className="lg:col-span-5 self-end">
            <p className="text-white/80 text-base md:text-lg leading-relaxed">Real photos from real AV Contracting job sites — before, during and after — across Vancouver Island and the Lower Mainland.</p>
          </div>
        </div>
      </section>

      <section className="bg-white sticky top-16 md:top-20 z-40 border-b border-[#2C3539]/10" data-testid="past-jobs-filters">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-3 md:py-4 flex gap-2 overflow-x-auto md:flex-wrap md:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {services.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`shrink-0 whitespace-nowrap px-4 py-2 text-xs tracking-[0.14em] uppercase font-semibold transition-colors ${
                filter === s
                  ? "bg-[#2F5F80] text-white"
                  : "bg-transparent text-[#23282B] border border-[#23282B]/30 hover:border-[#2F5F80] hover:text-[#2F5F80]"
              }`}
            >
              {s === "all" ? "All Work" : s}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#F1F3F5] py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16">
          {loading ? (
            <div className="font-display text-3xl text-[#2C3539]" data-testid="jobs-loading">LOADING…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="jobs-grid">
              {groups.map((group, idx) => (
                <JobGroupCard key={group[0].id || idx} group={group} idx={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#23282B] text-white" data-testid="past-jobs-outro">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-14 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 space-y-3 text-white/80 text-base leading-relaxed">
            <p>
              Every photo above is from a real AV Contracting job site — no stock imagery. You'll see excavations benched
              into Island bedrock, foundations formed and poured by our own crew, stamped and exposed-aggregate finishes,
              engineered retaining walls, and drainage systems installed to keep BC rain away from basements.
            </p>
            <p>
              Thinking about something similar on your property? Tell us which photo caught your eye and we'll walk you
              through how that job came together — timeline, challenges and all.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/contact" data-testid="past-jobs-cta" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-8 py-4 uppercase transition-colors">
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
